import React from 'react';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../../context/SiteContentContext';
import { useAnalytics } from '../../context/AnalyticsContext';

const Hero = () => {
  const { siteContent } = useSiteContent();
  const { trackEvent } = useAnalytics();
  const hero = siteContent?.hero || {};

  if (hero.visible === false) return null;

  const handleBookTrialClick = () => {
    trackEvent('book_trial_click', { location: 'hero_section' });
  };

  const handleExploreClick = () => {
    trackEvent('explore_programs_click', { location: 'hero_section' });
  };

  return (
    <section className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 md:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-section"
            className="text-center lg:text-left order-2 lg:order-1"
          >
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[3.25rem] font-semibold leading-snug sm:leading-tight tracking-tight text-slate-900 mb-4 sm:mb-6">
              {hero.headline || 'Building confident English speakers through patient, expert guidance.'}
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl leading-relaxed text-slate-600 mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0">
              {hero.subheadline || 'Personalized 1-on-1 online English lessons for students worldwide.'}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
              <Link
                to="/contact"
                onClick={handleBookTrialClick}
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center bg-slate-900 text-white hover:bg-slate-800 rounded-full px-7 sm:px-8 py-4 text-base font-medium transition-all shadow-sm hover:shadow-md"
              >
                {hero.ctaPrimary || 'Book a Free Trial'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
              <Link
                to="/programs"
                onClick={handleExploreClick}
                data-testid="hero-programs-button"
                className="inline-flex items-center justify-center bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-100 rounded-full px-7 sm:px-8 py-4 text-base font-medium transition-all"
              >
                {hero.ctaSecondary || 'Explore Program'}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative order-1 lg:order-2"
          >
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl shadow-slate-900/10">
              <img
                src={siteContent?.images?.heroImage || 'https://images.unsplash.com/photo-1758876019427-e2e5bf9a92c5?q=80&w=2070&auto=format&fit=crop'}
                alt="Online English tutor"
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/5 to-transparent pointer-events-none" />
            </div>
            <div className="hidden sm:block absolute -bottom-6 -left-6 w-24 h-24 bg-amber-100/60 rounded-full blur-2xl -z-10" />
            <div className="hidden sm:block absolute -top-6 -right-6 w-32 h-32 bg-slate-200/60 rounded-full blur-2xl -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
